import {
  DealerOptionItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto
} from '@api/machines'
import { MolsonBox, MolsonIcon, MolsonTypography } from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useCallback } from 'react'
import {
  ARCHIVED_ITEMS,
  formatPriceAndCurrency,
  matchOldPriceItem,
  STORED_ARCHIVED_ITEMS
} from '@/utils'
import { DealerOptionItemDtoWithChecked } from './DealerFitOptions'

type DealerFitsUtilsProps = {
  dealerFitName: string
  selectedOption: string | null
  selectedTag: string
  options: Record<string, DealerOptionItemDto[]>
  ungroupedOptions: DealerOptionItemDto[]
  selectedDealerOptions: DealerOptionItemDtoWithChecked[]
  quotationItem: MachineQuotationItemDto
  quotationItemOldPrices?: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
}

export const useDealerFitsUtils = ({
  dealerFitName,
  selectedOption,
  selectedTag,
  options,
  ungroupedOptions,
  selectedDealerOptions,
  quotationItem,
  quotationItemOldPrices
}: DealerFitsUtilsProps) => {
  const theme = molsonUseTheme()

  // Filter out brands that only have archived options
  const availableBrands = Object.entries(options).filter(([_, brandOptions]) =>
    brandOptions.some(option => !option.isArchived)
  )

  const hasMultipleBrands = availableBrands.length > 1
  const displayDropdown = hasMultipleBrands

  const filterOptions = () => {
    if (selectedOption) {
      return selectedTag
        ? options[selectedOption].filter(
            option => option?.subType2 === selectedTag
          )
        : options[selectedOption]
    }

    return []
  }

  const filterUngroupedOptions = () => {
    if (selectedTag) {
      return ungroupedOptions.filter(option => option?.subType2 === selectedTag)
    }

    return ungroupedOptions
  }

  const displayedOptions = displayDropdown
    ? filterOptions()
    : filterUngroupedOptions()

  const getItemQuantity = useCallback(
    (item: DealerOptionItemDto) => {
      return (quotationItem.dealerOptions ?? []).find(
        (option: DealerOptionItemDto) => option.id === item.id
      )?.quantity
    },
    [quotationItem.dealerOptions]
  )

  const getOptionChecked = (item: DealerOptionItemDto) => {
    if (!quotationItem) {
      return false
    }

    return quotationItem?.dealerOptions?.some(
      (dealerOption: DealerOptionItemDto) => item.id === dealerOption.id
    )
  }

  const getDisplayedOptions = () => {
    return displayedOptions?.filter(
      item =>
        !item.isArchived ||
        selectedDealerOptions.some(option => option.id === item.id)
    )
  }

  const getDealerFitOptionlabel = (item: DealerOptionItemDto) => {
    const { dnp = 0, id = 0 } = item
    const oldPrice = matchOldPriceItem(quotationItemOldPrices ?? {}, {
      id: id,
      price: dnp ?? 0,
      type: 'dealerOptions'
    })

    return (
      <MolsonBox display="flex" flexDirection="column">
        <MolsonBox alignItems="center" display="flex" gap={0.5}>
          {oldPrice !== undefined && (
            <>
              <MolsonTypography
                sx={{
                  color: theme.palette.secondary[500],
                  textDecoration: 'line-through'
                }}
                variant="body2"
              >
                {formatPriceAndCurrency(oldPrice)}
              </MolsonTypography>

              <MolsonIcon
                variant={oldPrice < dnp ? 'ARROW_UPWARD' : 'ARROW_DOWNWARD'}
                iconColor={oldPrice < dnp ? 'success' : 'error'}
              />
            </>
          )}

          <MolsonTypography
            sx={{
              color: theme.palette.secondary[600],
              fontWeight: 'bold'
            }}
            variant="body2"
          >
            {formatPriceAndCurrency(dnp)}
          </MolsonTypography>
        </MolsonBox>
      </MolsonBox>
    )
  }

  const archivedFamilies = quotationItem?.dealerOptions
    ?.filter(option => option.isArchived)
    .map(archivedItem => archivedItem.family)

  const revisedBrand = sessionStorage.getItem(STORED_ARCHIVED_ITEMS.BRAND)
  const revisedFamily = sessionStorage.getItem(STORED_ARCHIVED_ITEMS.FAMILY)

  const itemKey = `${ARCHIVED_ITEMS.DEALER_FIT}_${dealerFitName}`

  const selectedFamilyOptions = quotationItem?.dealerOptions?.filter(
    option => option.family === dealerFitName
  )

  let hasFamilyPriceDifference = false
  const getSummaryIcon = () => {
    if (archivedFamilies?.includes(dealerFitName)) {
      return { icon: 'ERROR_OUTLINE', color: 'error' }
    }
    if (hasFamilyPriceDifference) {
      return { icon: 'WARNING', color: 'warning' }
    }
    return undefined
  }

  const summaryIcon = getSummaryIcon()

  const selectedCount = selectedFamilyOptions?.length ?? 0

  return {
    getOptionChecked,
    getItemQuantity,
    getDealerFitOptionlabel,
    getDisplayedOptions,
    hasMultipleBrands,
    displayDropdown,
    archivedFamilies,
    revisedBrand,
    revisedFamily,
    itemKey,
    selectedFamilyOptions,
    hasFamilyPriceDifference,
    summaryIcon,
    selectedCount
  }
}
